/**
 * Podstawianie wyrażeń układu — jedyne miejsce, w którym `Wyrazenie` zamienia
 * się w `string`.
 *
 * Reguła, dla której ten plik jest osobny od interpretera: BRAK ZMIENNEJ
 * NIEOPCJONALNEJ MA RZUCIĆ, i to z KOMPLETEM braków naraz. Ta sama zasada, co
 * w `render.ts` katalogu i w katalogu poczty — pusta etykieta wysyłkowa, która
 * fizycznie wyszła z drukarki, jest gorsza niż nieudane zadanie. Żeby zebrać
 * komplet, podstawianie musi przejść CAŁY układ, notując braki po drodze,
 * i dopiero potem rzucić — czyli potrzebuje akumulatora, a nie zwracania
 * błędu z pojedynczego wyrażenia.
 */

import type { PrintVars } from '../types';

import {
  jestLit,
  jestPole,
  jestTeraz,
  jestVar,
  type KsztaltTeraz,
  type LayoutNode,
  type PrintLayout,
  type Wyrazenie,
} from './schemat';

/**
 * Strefa, w której liczy się chwilę wydruku.
 *
 * Stała, nie parametr, i to jest decyzja: render leci na serwerze chodzącym
 * w UTC, a etykieta wychodzi z drukarki stojącej w hali. Bez jawnej strefy
 * naklejka wydrukowana o 00:30 czasu lokalnego niosłaby datę DNIA
 * POPRZEDNIEGO — na etykiecie produkcyjnej albo na dacie przydatności jest to
 * najgorsza z możliwych pomyłek, bo wygląda dokładnie jak dobra.
 */
const STREFA = 'Europe/Warsaw';

/**
 * Chwila wydruku w postaci, którą da się odczytać z naklejki.
 *
 * Format jest ASCII i stały (`31.12.2026`, `23:59`), bo idzie także na
 * drukarkę termiczną: `Intl` z lokalizacją `pl-PL` potrafi wstawić spację
 * niełamliwą (U+202F) między składniki, a transliteracja termiczna zamieniłaby
 * ją na znak zapytania. Dlatego składamy z części, a nie z gotowego
 * `toLocaleString`.
 */
export function formatujTeraz(ksztalt: KsztaltTeraz, chwila: Date): string {
  const czesci = new Intl.DateTimeFormat('pl-PL', {
    timeZone: STREFA,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(chwila);
  const daj = (typ: Intl.DateTimeFormatPartTypes): string =>
    czesci.find((c) => c.type === typ)?.value ?? '';
  const data = `${daj('day')}.${daj('month')}.${daj('year')}`;
  // `hourCycle` przy `hour12: false` daje w niektórych środowiskach „24" dla
  // północy — stąd normalizacja, a nie zaufanie do formatera.
  const godzina = `${daj('hour') === '24' ? '00' : daj('hour')}:${daj('minute')}`;
  switch (ksztalt) {
    case 'data':
      return data;
    case 'godzina':
      return godzina;
    case 'data_godzina':
      return `${data} ${godzina}`;
  }
}

/**
 * Kontekst jednego przebiegu podstawiającego. Tworzy go interpreter i niesie
 * przez cały układ — akumulatory `braki`/`nieznane` są celowo mutowalne,
 * bo ich sensem jest zebranie WSZYSTKIEGO przed rzuceniem.
 */
export interface KontekstPodstawienia {
  vars: PrintVars;
  /**
   * Bieżąca pozycja listy — źródło tokenów `{pole}`. Poza wierszem listy
   * `undefined`, i wtedy `{pole}` jest błędem, nie pustym stringiem.
   */
  pozycja?: Record<string, unknown>;
  /**
   * Zmienne, które wzór deklaruje. `null` = katalog nieznany (np. podgląd
   * ręcznie sklejonego układu) — wtedy nie da się odróżnić literówki od
   * zmiennej opcjonalnej, więc `nieznane` zostaje puste.
   */
  znane: ReadonlySet<string> | null;
  /** Zmienne nieopcjonalne wzoru. Bez katalogu: każda użyta zmienna jest wymagana. */
  wymagane: ReadonlySet<string> | null;
  /** Akumulator: zmienne wymagane bez wartości. */
  braki: Set<string>;
  /** Akumulator: zmienne spoza `znane`. */
  nieznane: Set<string>;
  /** Akumulator: tokeny `{pole}` użyte poza wierszem listy. */
  poleBezListy: Set<string>;
  /**
   * Akumulator: tokeny `{pole}` wskazujące pole, którego pozycja listy NIE MA.
   *
   * Osobno od `poleBezListy`, bo to inna wada i inna podpowiedź: tam token
   * stoi w złym miejscu układu, tutaj stoi dobrze, ale nazwa pola nie zgadza
   * się z kształtem danych (`{pole:'nazwa'}` przy pozycjach `{name, qty}`).
   * Bez tego akumulatora literówka w nazwie pola drukowała sześć pustych
   * wierszy — dokładnie ta klasa cichej straty, przed którą broni `braki`.
   */
  polaBezWartosci: Set<string>;
  /**
   * Chwila, którą podstawiają tokeny `{teraz}`. JEDNA na cały przebieg —
   * etykieta z nagłówkiem „23:59" i stopką „00:00" jest zagadką dla tego, kto
   * ją potem czyta, a przy dacie zmienia dodatkowo dzień.
   *
   * `undefined` znaczy „wołający nie podał zegara" i jest błędem, a nie pustym
   * miejscem: pusta data na etykiecie produkcyjnej wygląda jak brak wymagań,
   * a nie jak awaria.
   */
  chwila?: Date;
  /** Akumulator: użyto `{teraz}`, a wołający nie podał chwili. */
  brakZegara: Set<KsztaltTeraz>;
}

/** Czy wartość liczy się jako „podana" (ta sama semantyka co `has()` w types.ts). */
function jestWartosc(raw: unknown): boolean {
  if (raw === null || raw === undefined) return false;
  if (typeof raw === 'string') return raw.length > 0;
  return true;
}

function naTekst(raw: unknown): string {
  if (raw === null || raw === undefined) return '';
  return String(raw);
}

/**
 * Sprawdza JEDNO wiązanie do zmiennej wzoru i notuje wynik w akumulatorach.
 * Zwraca `true`, gdy wartość jest i wolno jej użyć.
 *
 * Wydzielone z `podstaw()`, bo wiązania żyją nie tylko w wyrażeniach: źródło
 * `lista` i źródło obrazu `zmienna` też wskazują zmienną wzoru. Dopóki tę
 * ścieżkę znał wyłącznie `podstaw()`, brak wartości dla `allergens` nie trafiał
 * do `braki` i etykieta partii drukowała się BEZ alergenów — zamiast zatrzymać
 * zadanie, tak jak robi to katalog.
 */
export function sprawdzZmienna(nazwa: string, ctx: KontekstPodstawienia): boolean {
  if (ctx.znane && !ctx.znane.has(nazwa)) {
    ctx.nieznane.add(nazwa);
    return false;
  }
  if (!jestWartosc(ctx.vars[nazwa])) {
    // Bez katalogu każda użyta zmienna jest wymagana: układ, który
    // odwołuje się do pustej zmiennej, drukuje puste pole — dokładnie
    // to, czemu ta bramka ma zapobiegać.
    if (!ctx.wymagane || ctx.wymagane.has(nazwa)) ctx.braki.add(nazwa);
    return false;
  }
  return true;
}

/**
 * Wyrażenie → tekst. Notuje braki w kontekście zamiast rzucać, żeby wołający
 * (interpreter) mógł zebrać komplet z całego układu.
 */
export function podstaw(w: Wyrazenie, ctx: KontekstPodstawienia): string {
  let out = '';
  for (const token of w) {
    if (jestLit(token)) {
      out += token.lit;
      continue;
    }
    if (jestVar(token)) {
      const nazwa = token.var;
      if (!sprawdzZmienna(nazwa, ctx)) continue;
      out += naTekst(ctx.vars[nazwa]);
      continue;
    }
    if (jestTeraz(token)) {
      if (!ctx.chwila) {
        ctx.brakZegara.add(token.teraz);
        continue;
      }
      out += formatujTeraz(token.teraz, ctx.chwila);
      continue;
    }
    if (jestPole(token)) {
      if (!ctx.pozycja) {
        ctx.poleBezListy.add(token.pole);
        continue;
      }
      // Pozycja bez tego pola to literówka w układzie albo rozjazd kształtu
      // danych. Cichy pusty string wydrukowałby listę pustych punktorów.
      if (!Object.hasOwn(ctx.pozycja, token.pole) || !jestWartosc(ctx.pozycja[token.pole])) {
        ctx.polaBezWartosci.add(token.pole);
        continue;
      }
      out += naTekst(ctx.pozycja[token.pole]);
      continue;
    }
    // Token spoza gramatyki — walidator zgłasza to osobno; tutaj pomijamy,
    // żeby przebieg zebrał komplet problemów, a nie wywalił się na pierwszym.
  }
  return out;
}

/** Wszystkie tokeny `{var}` w jednym wyrażeniu. */
function zmienneWyrazenia(w: Wyrazenie): string[] {
  return w.filter(jestVar).map((t) => t.var);
}

function zmienneWezla(node: LayoutNode, out: Set<string>): void {
  // Symetria z interpreterem (`uklad/interpreter.ts:77`, `if (node.ukryty) return []`):
  // węzeł ukryty NIE MALUJE SIĘ, więc jego wiązania nie są użyciem zmiennej.
  // Zejście w dzieci też odpada — interpreter porzuca całe poddrzewo.
  if (node.ukryty) return;

  switch (node.kind) {
    case 'text':
      for (const v of zmienneWyrazenia(node.tekst)) out.add(v);
      return;
    case 'barcode':
    case 'qr':
      for (const v of zmienneWyrazenia(node.dane)) out.add(v);
      return;
    case 'image':
      if (node.zrodlo.rodzaj === 'zmienna') out.add(node.zrodlo.var);
      return;
    case 'repeat':
      switch (node.zrodlo.rodzaj) {
        case 'literalne':
          for (const wiersz of node.zrodlo.wiersze)
            for (const dziecko of wiersz) zmienneWezla(dziecko, out);
          return;
        case 'zawijanie':
          for (const v of zmienneWyrazenia(node.zrodlo.tekst)) out.add(v);
          return;
        case 'lista':
          out.add(node.zrodlo.var);
          for (const dziecko of node.zrodlo.wiersz) zmienneWezla(dziecko, out);
          return;
      }
      return;
    case 'line':
    case 'box':
      return;
  }
}

/**
 * Zmienne wzoru użyte w układzie — posortowane.
 *
 * Do czego to służy w panelu: zmienna NIEOPCJONALNA, której układ NIE używa,
 * świeci na czerwono i blokuje publikację. Bez tej listy edytor pozwoliłby
 * opublikować etykietę partii bez numeru partii, a wada wyszłaby dopiero
 * z drukarki.
 *
 * `ukryty: true` NIE liczy się jako użycie i to jest cała pointa tej funkcji.
 * Zmierzone: układ zasiany z `fabrica.batch_label`, w którym jedyny węzeł
 * wiążący `batch_number` dostaje `ukryty: true` (jedno kliknięcie „oka"
 * w panelu warstw), przechodził publikację na zielono, a `renderPrint(...,
 * { tenantLayout })` bez `batch_number` kończył się OK — podczas gdy tor
 * katalogu dla tych samych zmiennych rzucał `MissingPrintVariableError`.
 * `ukryty` był JEDYNYM stanem, w którym zmienna była jednocześnie „związana"
 * dla bramki publikacji i „nieużyta" dla akumulatora `braki` przy renderze;
 * skutkiem była etykieta partii bez numeru partii, fizycznie nie do odróżnienia
 * od dobrej. Interpreter porzuca ukryty węzeł (`interpreter.ts:77`), więc
 * bramka musi patrzeć tak samo.
 */
export function uzyteZmienne(layout: PrintLayout): string[] {
  const out = new Set<string>();
  for (const node of layout.nodes) zmienneWezla(node, out);
  return [...out].sort();
}
